// Standardized component styles for consistent UI across the app

// Shared container style for database views
export const containerStyles = {
  root: {
    width: '100%',
    py: 0,
    px: 0,
    '& .MuiPaper-root': {
      boxShadow: 'none',
      borderRadius: 0,
      backgroundColor: 'transparent',
    },
    '& .MuiTableContainer-root': {
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    '& .MuiTable-root': {
      backgroundColor: 'transparent',
    },
  },
};

export const tooltipStyles = {
  tooltip: {
    backgroundColor: '#558b2f', // Darker green to match footer
    color: '#ffffff', // White text for better contrast
    fontSize: '0.75rem',
    padding: '8px 12px',
    borderRadius: '4px',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
    fontWeight: 500,
    '& .MuiTooltip-arrow': {
      color: '#558b2f', // Match tooltip background
    },
  },
  arrow: {
    color: '#558b2f', // Match tooltip background
  },
};

export const tableCellStyles = {
  root: {
    border: 'none',
    padding: '4px 8px', // Reduced vertical padding to 4px, kept horizontal at 8px
    verticalAlign: 'middle',
    fontSize: '0.875rem',
    '&.MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      padding: '8px 12px', // Slightly more padding for header cells
    },
  },
};

export const avatarStyles = {
  root: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    maxWidth: 40,
    maxHeight: 40,
    borderRadius: '4px',
    '& img': {
      width: '100% !important',
      height: '100% !important',
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      objectFit: 'contain !important',
      objectPosition: 'center',
      display: 'block !important',
      p: 0,
      margin: 'auto',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '4px',
    },
  },
  // Standard style that enforces consistent sizing
  standard: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    maxWidth: 40,
    maxHeight: 40,
    '& img': {
      width: '100% !important',
      height: '100% !important',
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      objectFit: 'contain !important',
      objectPosition: 'center',
      display: 'block !important',
      p: 0,
      margin: 'auto',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '4px',
    },
  },
  // Hover style removed as per request
  hover: {},
  // For smaller avatars
  small: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    maxWidth: 40,
    maxHeight: 40,
    borderRadius: '4px',
  },
  // For larger avatars
  large: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    maxWidth: 40,
    maxHeight: 40,
    borderRadius: '4px',
  },
  // For square avatars with consistent corner radius
  square: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    maxWidth: 40,
    maxHeight: 40,
    borderRadius: '4px',
  },
};

export const tabStyles = {
  // Tab container styles
  container: {
    mb: 0,
    '& .MuiTabs-flexContainer': {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      gap: 0,
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  // Individual tab styles
  root: {
    textTransform: 'none',
    width: '400px',
    minWidth: '400px',
    maxWidth: '400px',
    fontWeight: 500,
    minHeight: 80,
    height: 80,
    maxHeight: 80,
    borderRadius: 0,
    boxShadow: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    '&.Mui-selected': {
      color: '#388e3c',
      fontWeight: 600,
      background: 'none',
      border: 'none',
      boxShadow: 'none',
    },
    '&:hover': {
      color: '#388e3c',
      opacity: 1,
      background: 'none',
    },
    '&.Mui-focusVisible': {
      background: 'none',
      outline: 'none',
    },
  },
  // Tab content container
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    width: '100%',
    padding: 0,
    height: 80,
    minHeight: 80,
    maxHeight: 80,
    '& .MuiAvatar-root': {
      margin: '0 auto',
      display: 'block',
      flexShrink: 0,  // Prevent avatar from shrinking
    },
    '& .MuiTypography-root': {
      textAlign: 'center',
      width: '100%',
      lineHeight: 1.1,  // Tighter line height for the label
      marginTop: 0,     // Remove any top margin
      padding: '2px 0'   // Small vertical padding for better text alignment
    }
  },
  // Tab label text
  label: {
    fontSize: '0.875rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    fontWeight: 600,
  }
};


export const tabContainerStyles = {
  outer: {
    width: '100%',
    minHeight: '100vh',
    pt: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.1)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },
    pb: 2,
  },
  inner: {
    width: '100%',
    maxWidth: 1200,
    mx: 'auto',
    px: 0,  // Remove horizontal padding
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};

export const typographyStyles = {
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.43,
    color: 'text.secondary',
  },
};

// Shared gap for main cell content (avatar + text)
export const mainCellGap = 1; // 8px, matches Animals table for compactness
